import type {
    AnalyticsRangeKey,
    DailyMetric,
    MonthlyMetric,
    PlanId,
    RangeKey
} from '@/types'

import { createRandom, hashString } from '@/lib/random'
import { CUSTOMER_STATS, PLAN_BREAKDOWN, PROVINCE_BREAKDOWN } from '@/data/customers'
import { PLANS } from '@/data/plans'

import {
    TODAY,
    addDays,
    addMonths,
    endOfMonth,
    formatDate,
    formatDateShort,
    formatMonthLong,
    formatMonthShort,
    startOfMonth
} from '@/lib/date'

import { ANALYTICS_RANGE_OPTIONS, RANGE_OPTIONS } from '@/constants/dashboard'

// Daily history of MRR and customers, generated backwards from today's real totals in
// the customer data so every number on every page agrees with the customer list.

type NumericKey = Exclude<keyof DailyMetric, 'date'>

const HISTORY_DAYS = 800

const round1000 = (value: number): number => Math.round(value / 1000) * 1000

const percentChange = (value: number, previous: number): number => (previous === 0 ? 0 : ((value - previous) / previous) * 100)

const createDailyMetrics = (): DailyMetric[] => {
    const random = createRandom(9202611)
    const reversed: DailyMetric[] = []
    let mrr = CUSTOMER_STATS.mrr
    let customers = CUSTOMER_STATS.paying

    for (let offset = 0; offset < HISTORY_DAYS; offset++) {
        const growth = 0.00178 + random.float(-0.0009, 0.0009)
        const previousMrr = mrr / (1 + growth)
        const churnedMrr = round1000(previousMrr * (0.00062 + random.float(-0.00025, 0.00035)))
        const contractionMrr = round1000(previousMrr * (0.00012 + random.float(-0.00006, 0.00008)))
        const expansionMrr = round1000(previousMrr * (0.00085 + random.float(-0.0003, 0.0004)))
        const newMrr = Math.max(0, round1000(mrr - previousMrr + churnedMrr + contractionMrr - expansionMrr))
        const churnedCustomers = Math.max(0, Math.round(customers * 0.00064 + random.float(-1.2, 1.2)))
        const newCustomers = Math.max(0, Math.round(customers * 0.00204 + random.float(-1.6, 1.6)))

        reversed.push({
            date: addDays(TODAY, -offset),
            mrr,
            customers,
            newCustomers,
            churnedCustomers,
            newMrr,
            expansionMrr,
            contractionMrr,
            churnedMrr
        })

        mrr -= newMrr + expansionMrr - contractionMrr - churnedMrr
        customers -= newCustomers - churnedCustomers
    }

    return reversed.reverse()
}

export const DAILY_METRICS: DailyMetric[] = createDailyMetrics()

const LAST_INDEX = DAILY_METRICS.length - 1

const INDEX_BY_DATE = new Map(DAILY_METRICS.map((metric, index) => [metric.date, index]))

const indexOf = (date: string): number => INDEX_BY_DATE.get(date) ?? (date > TODAY ? LAST_INDEX : 0)

const sumRange = (from: number, to: number, key: NumericKey): number => {
    let total = 0

    for (let index = Math.max(0, from); index <= to; index++) total += DAILY_METRICS[index][key]

    return total
}

// Customer churn over a span of days, expressed as a monthly rate in percent
const churnRate = (from: number, to: number): number => {
    const base = DAILY_METRICS[Math.max(0, from - 1)].customers
    const days = to - from + 1

    return base === 0 ? 0 : (sumRange(from, to, 'churnedCustomers') / base) * (30 / days) * 100
}

const rollingChurn = (index: number): number => churnRate(index - 29, index)

export type TrendPoint = {
    key: string
    label: string
    tooltipLabel: string
    value: number
    previous: number
    previousLabel: string
}

export type KpiMetric = {
    value: number
    previous: number
    change: number
    spark: number[]
}

export type MrrMovement = {
    newMrr: number
    expansionMrr: number
    contractionMrr: number
    churnedMrr: number
    net: number
}

export type OverviewData = {
    range: RangeKey
    label: string
    comparison: string
    startDate: string
    endDate: string
    trend: TrendPoint[]
    kpis: {
        mrr: KpiMetric
        customers: KpiMetric
        churn: KpiMetric
        arpu: KpiMetric
    }
    movement: MrrMovement
    newCustomers: number
    churnedCustomers: number
}

const buildOverview = (range: RangeKey): OverviewData => {
    const option = RANGE_OPTIONS.find((item) => item.value === range) ?? RANGE_OPTIONS[1]
    const days = option.days
    const start = LAST_INDEX - days
    const from = start + 1
    const now = DAILY_METRICS[LAST_INDEX]
    const then = DAILY_METRICS[start]

    // Indexes of the points drawn in the chart and sparklines
    let pointIndexes: number[]
    let trend: TrendPoint[]

    if (range === '12m') {
        pointIndexes = []
        trend = Array.from({ length: 12 }, (_, position) => {
            const monthsBack = 11 - position
            const month = startOfMonth(addMonths(TODAY, -monthsBack))
            const isCurrent = monthsBack === 0
            const date = isCurrent ? TODAY : endOfMonth(month)
            const previousDate = isCurrent ? addMonths(TODAY, -12) : endOfMonth(addMonths(month, -12))
            const index = indexOf(date)

            pointIndexes.push(index)

            return {
                key: month,
                label: formatMonthShort(month),
                tooltipLabel: isCurrent ? `${formatMonthLong(month)} (berjalan)` : formatMonthLong(month),
                value: DAILY_METRICS[index].mrr,
                previous: DAILY_METRICS[indexOf(previousDate)].mrr,
                previousLabel: isCurrent ? formatDate(previousDate) : formatMonthLong(previousDate)
            }
        })
    }
    else {
        pointIndexes = Array.from({ length: days }, (_, position) => from + position)
        trend = pointIndexes.map((index) => {
            const metric = DAILY_METRICS[index]
            const previous = DAILY_METRICS[index - days]

            return {
                key: metric.date,
                label: formatDateShort(metric.date),
                tooltipLabel: formatDate(metric.date),
                value: metric.mrr,
                previous: previous.mrr,
                previousLabel: formatDate(previous.date)
            }
        })
    }

    const churnNow = churnRate(from, LAST_INDEX)
    const churnBefore = churnRate(from - days, start)
    const arpuNow = now.mrr / now.customers
    const arpuBefore = then.mrr / then.customers

    const movement = {
        newMrr: sumRange(from, LAST_INDEX, 'newMrr'),
        expansionMrr: sumRange(from, LAST_INDEX, 'expansionMrr'),
        contractionMrr: sumRange(from, LAST_INDEX, 'contractionMrr'),
        churnedMrr: sumRange(from, LAST_INDEX, 'churnedMrr'),
        net: now.mrr - then.mrr
    }

    return {
        range,
        label: option.label,
        comparison: option.comparison,
        startDate: DAILY_METRICS[from].date,
        endDate: now.date,
        trend,
        kpis: {
            mrr: {
                value: now.mrr,
                previous: then.mrr,
                change: percentChange(now.mrr, then.mrr),
                spark: pointIndexes.map((index) => DAILY_METRICS[index].mrr)
            },
            customers: {
                value: now.customers,
                previous: then.customers,
                change: now.customers - then.customers,
                spark: pointIndexes.map((index) => DAILY_METRICS[index].customers)
            },
            churn: {
                value: churnNow,
                previous: churnBefore,
                change: churnNow - churnBefore,
                spark: pointIndexes.map((index) => rollingChurn(index))
            },
            arpu: {
                value: arpuNow,
                previous: arpuBefore,
                change: percentChange(arpuNow, arpuBefore),
                spark: pointIndexes.map((index) => DAILY_METRICS[index].mrr / DAILY_METRICS[index].customers)
            }
        },
        movement,
        newCustomers: sumRange(from, LAST_INDEX, 'newCustomers'),
        churnedCustomers: sumRange(from, LAST_INDEX, 'churnedCustomers')
    }
}

const OVERVIEW_CACHE = new Map<RangeKey, OverviewData>()

export const getOverview = (range: RangeKey): OverviewData => {
    const cached = OVERVIEW_CACHE.get(range)
    if (cached) return cached

    const data = buildOverview(range)
    OVERVIEW_CACHE.set(range, data)
    return data
}

// Monthly history (complete months only)
const createMonthlyMetrics = (): MonthlyMetric[] => {
    const months: MonthlyMetric[] = []
    const currentMonth = startOfMonth(TODAY)
    let month = startOfMonth(addMonths(DAILY_METRICS[0].date, 1))

    while (month < currentMonth) {
        const from = indexOf(month)
        const to = indexOf(endOfMonth(month))

        months.push({
            month,
            mrrStart: DAILY_METRICS[from - 1].mrr,
            mrrEnd: DAILY_METRICS[to].mrr,
            customersStart: DAILY_METRICS[from - 1].customers,
            customersEnd: DAILY_METRICS[to].customers,
            newCustomers: sumRange(from, to, 'newCustomers'),
            churnedCustomers: sumRange(from, to, 'churnedCustomers'),
            newMrr: sumRange(from, to, 'newMrr'),
            expansionMrr: sumRange(from, to, 'expansionMrr'),
            contractionMrr: sumRange(from, to, 'contractionMrr'),
            churnedMrr: sumRange(from, to, 'churnedMrr')
        })

        month = addMonths(month, 1)
    }

    return months
}

export const MONTHLY_METRICS: MonthlyMetric[] = createMonthlyMetrics()

const CURRENT_PLAN_SHARES = (() => {
    const total = PLAN_BREAKDOWN.reduce((sum, item) => sum + item.mrr, 0)
    return PLAN_BREAKDOWN.map((item) => (total === 0 ? 0.25 : item.mrr / total))
})()

// Lower tiers used to be a bigger share of revenue; the mix drifts towards today's split
const PLAN_SHARE_DRIFT = [0.05, 0.02, -0.01, -0.06]

const splitByPlan = (mrr: number, monthsAgo: number): Record<PlanId, number> => {
    const drift = Math.min(1, monthsAgo / 24)
    const result = {} as Record<PlanId, number>
    let assigned = 0

    PLANS.forEach((plan, index) => {
        if (index === PLANS.length - 1) {
            result[plan.id] = mrr - assigned
            return
        }

        const share = CURRENT_PLAN_SHARES[index] + PLAN_SHARE_DRIFT[index] * drift
        const value = round1000(mrr * share)
        result[plan.id] = value
        assigned += value
    })

    return result
}

export type AnalyticsMonth = MonthlyMetric & {
    label: string
    longLabel: string
    byPlan: Record<PlanId, number>
    trials: number
}

export type Cohort = {
    month: string
    label: string
    size: number
    retention: number[]
}

export type AnalyticsKpi = {
    value: number
    previous: number
}

export type AnalyticsData = {
    range: AnalyticsRangeKey
    label: string
    months: AnalyticsMonth[]
    kpis: {
        nrr: AnalyticsKpi
        ltv: AnalyticsKpi
        cac: AnalyticsKpi
        trialConversion: AnalyticsKpi
        payback: AnalyticsKpi
    }
    funnel: { label: string, description: string, value: number }[]
    channels: { label: string, value: number }[]
    cohorts: Cohort[]
}

const CAC_BY_RANGE: Record<AnalyticsRangeKey, [number, number]> = {
    '3m': [7400000, 7900000],
    '6m': [7150000, 7600000],
    '12m': [6820000, 7300000]
}

const CHANNELS: { label: string, share: number }[] = [
    { label: 'Organik dan SEO', share: 0.34 },
    { label: 'Referral pelanggan', share: 0.22 },
    { label: 'Iklan berbayar', share: 0.18 },
    { label: 'Mitra dan reseller', share: 0.14 },
    { label: 'Media sosial', share: 0.12 }
]

const CHANNEL_DRIFT: Record<AnalyticsRangeKey, number[]> = {
    '3m': [0.02, -0.01, 0.01, -0.01, -0.01],
    '6m': [0.01, 0, 0, 0, -0.01],
    '12m': [0, 0, 0, 0, 0]
}

const GROSS_MARGIN = 0.8

const trialConversionFor = (month: string): number => createRandom(hashString(`trial-${month}`)).float(0.29, 0.34)

const retentionFor = (month: string): number[] => {
    const random = createRandom(hashString(`cohort-${month}`))
    const retention = [100]
    const decayRanges: [number, number][] = [[6, 10], [3, 5.5], [2, 4]]

    for (let offset = 1; offset < 12; offset++) {
        const [min, max] = decayRanges[offset - 1] ?? [0.8, 2.4]
        const next = Math.max(0, retention[offset - 1] - random.float(min, max))
        retention.push(Math.round(next * 10) / 10)
    }

    return retention
}

// Splits a total into whole numbers that add up exactly (largest remainder)
const splitWhole = (total: number, shares: number[]): number[] => {
    const raw = shares.map((share) => total * share)
    const result = raw.map((value) => Math.floor(value))
    let remainder = total - result.reduce((sum, value) => sum + value, 0)

    const order = raw
        .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
        .sort((a, b) => b.fraction - a.fraction)

    for (const item of order) {
        if (remainder <= 0) break
        result[item.index] += 1
        remainder -= 1
    }

    return result
}

const summarize = (window: MonthlyMetric[]) => {
    const first = window[0]
    const last = window[window.length - 1]
    const expansion = window.reduce((sum, month) => sum + month.expansionMrr, 0)
    const contraction = window.reduce((sum, month) => sum + month.contractionMrr, 0)
    const churned = window.reduce((sum, month) => sum + month.churnedMrr, 0)
    const churnedCustomers = window.reduce((sum, month) => sum + month.churnedCustomers, 0)
    const newCustomers = window.reduce((sum, month) => sum + month.newCustomers, 0)
    const trials = window.reduce((sum, month) => sum + Math.round(month.newCustomers / trialConversionFor(month.month)), 0)
    const averageCustomers = (first.customersStart + last.customersEnd) / 2
    const monthlyChurn = churnedCustomers / averageCustomers / window.length
    const arpu = last.mrrEnd / last.customersEnd

    return {
        nrr: ((first.mrrStart + expansion - contraction - churned) / first.mrrStart) * 100,
        ltv: monthlyChurn === 0 ? 0 : (arpu * GROSS_MARGIN) / monthlyChurn,
        trialConversion: trials === 0 ? 0 : (newCustomers / trials) * 100,
        arpu,
        newCustomers,
        trials
    }
}

const buildAnalytics = (range: AnalyticsRangeKey): AnalyticsData => {
    const option = ANALYTICS_RANGE_OPTIONS.find((item) => item.value === range) ?? ANALYTICS_RANGE_OPTIONS[1]
    const count = option.months
    const total = MONTHLY_METRICS.length
    const window = MONTHLY_METRICS.slice(total - count)
    const previousWindow = MONTHLY_METRICS.slice(total - count * 2, total - count)
    const current = summarize(window)
    const previous = summarize(previousWindow)
    const [cac, previousCac] = CAC_BY_RANGE[range]

    const months: AnalyticsMonth[] = window.map((month, position) => ({
        ...month,
        label: formatMonthShort(month.month),
        longLabel: formatMonthLong(month.month),
        byPlan: splitByPlan(month.mrrEnd, count - 1 - position),
        trials: Math.round(month.newCustomers / trialConversionFor(month.month))
    }))

    const shares = CHANNELS.map((channel, index) => channel.share + CHANNEL_DRIFT[range][index])
    const channelCounts = splitWhole(current.newCustomers, shares)
    const activated = Math.round(current.trials * 0.58)

    const cohorts: Cohort[] = window.map((month, position) => ({
        month: month.month,
        label: formatMonthLong(month.month),
        size: month.newCustomers,
        retention: retentionFor(month.month).slice(0, count - position)
    }))

    return {
        range,
        label: option.label,
        months,
        kpis: {
            nrr: { value: current.nrr, previous: previous.nrr },
            ltv: { value: current.ltv, previous: previous.ltv },
            cac: { value: cac, previous: previousCac },
            trialConversion: { value: current.trialConversion, previous: previous.trialConversion },
            payback: {
                value: cac / (current.arpu * GROSS_MARGIN),
                previous: previousCac / (previous.arpu * GROSS_MARGIN)
            }
        },
        funnel: [
            { label: 'Pengunjung situs', description: 'Mengunjungi halaman harga atau produk', value: Math.round(current.trials / 0.0235) },
            { label: 'Daftar trial', description: 'Membuat akun dan memulai trial 14 hari', value: current.trials },
            { label: 'Aktivasi produk', description: 'Mencatat transaksi pertama selama trial', value: activated },
            { label: 'Berlangganan', description: 'Memilih paket berbayar', value: current.newCustomers }
        ],
        channels: CHANNELS.map((channel, index) => ({ label: channel.label, value: channelCounts[index] })),
        cohorts
    }
}

const ANALYTICS_CACHE = new Map<AnalyticsRangeKey, AnalyticsData>()

export const getAnalytics = (range: AnalyticsRangeKey): AnalyticsData => {
    const cached = ANALYTICS_CACHE.get(range)
    if (cached) return cached

    const data = buildAnalytics(range)
    ANALYTICS_CACHE.set(range, data)
    return data
}

// Quarter goals shown on the overview
const quarterStart = '2026-10-01'

export const QUARTER_GOALS = (() => {
    const now = DAILY_METRICS[LAST_INDEX]
    const quarterNewCustomers = sumRange(indexOf(quarterStart), LAST_INDEX, 'newCustomers')

    return {
        label: 'Q4 2026',
        endsAt: '2026-12-31',
        mrr: { current: now.mrr, target: 2000000000 },
        newCustomers: { current: quarterNewCustomers, target: 480 },
        churn: { current: rollingChurn(LAST_INDEX), target: 2 }
    }
})()

// Paying customers per province: the top five plus everything else
export const REGION_BREAKDOWN = (() => {
    const top = PROVINCE_BREAKDOWN.slice(0, 5)
    const others = PROVINCE_BREAKDOWN.slice(5).reduce((sum, item) => sum + item.customers, 0)
    const total = PROVINCE_BREAKDOWN.reduce((sum, item) => sum + item.customers, 0)

    return {
        total,
        rows: [...top.map((item) => ({ label: item.province, value: item.customers })), { label: 'Provinsi lainnya', value: others }]
    }
})()
